import { useEffect, useRef, useState } from 'react';
import { X, Send, Loader2, MessageSquare, MessagesSquare } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { SelectField } from '@/components/ui/SelectField';

// ────────────────────────────────────────────────────────────────────────────
// SendMessageModal — single dialog for ad-hoc SMS / WhatsApp from a lead row.
//
// Reused for both channels because the form is identical (recipient phone,
// message body, send button) — only the endpoint and the per-channel limit
// differ. The 402 upgrade modal handles plan-gating: the parent component
// doesn't need to filter the action menu — calling /sms/send or
// /whatsapp/send on a free-plan tenant will surface the upgrade prompt.
// ────────────────────────────────────────────────────────────────────────────

export type MessageChannel = 'sms' | 'whatsapp';

interface Props {
  channel: MessageChannel;
  leadId: string;
  leadName: string;
  phone: string;
  onClose: () => void;
  onSent?: () => void;
}

const CHANNEL_META: Record<MessageChannel, {
  label: string;
  icon: typeof MessageSquare;
  iconColor: string;
  bgColor: string;
  ringColor: string;
  endpoint: string;
  bodyKey: 'message';
  charLimit: number;
  charBucketHelp: string;
}> = {
  sms: {
    label: 'Send SMS',
    icon: MessageSquare,
    iconColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
    ringColor: 'ring-orange-100',
    endpoint: '/sms/send',
    bodyKey: 'message',
    charLimit: 1000,
    // 160 chars = 1 SMS segment for GSM-7. Above that the carrier splits
    // into multiple segments and you pay per segment. Tell the user.
    charBucketHelp: '160 chars = 1 SMS segment. Longer messages are split into multiple segments (each charged separately).',
  },
  whatsapp: {
    label: 'Send WhatsApp',
    icon: MessagesSquare,
    iconColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    ringColor: 'ring-emerald-100',
    endpoint: '/whatsapp/send',
    bodyKey: 'message',
    charLimit: 4096,
    charBucketHelp: 'WhatsApp allows up to 4096 characters per message. Outside the 24-hour customer service window, only pre-approved templates can be sent.',
  },
};

interface SmsTemplateOpt { id: string; name: string; body: string; isActive: boolean }

export function SendMessageModal({ channel, leadId, leadName, phone, onClose, onSent }: Props) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  // WhatsApp: resolve the first connected account ID once on open.
  const waAccountIdRef = useRef<string | null>(null);
  const [waAccountLoading, setWaAccountLoading] = useState(channel === 'whatsapp');
  // SMS: reusable templates the user can pick from to fill the message.
  const [smsTemplates, setSmsTemplates] = useState<SmsTemplateOpt[]>([]);

  const meta = CHANNEL_META[channel];
  const Icon = meta.icon;

  // Load SMS templates once for the SMS channel so the user can prefill from
  // a saved template (see /sms-templates).
  useEffect(() => {
    if (channel !== 'sms') return;
    api.get('/sms-templates')
      .then((r) => setSmsTemplates((Array.isArray(r.data) ? r.data : []).filter((t: SmsTemplateOpt) => t.isActive)))
      .catch(() => setSmsTemplates([]));
  }, [channel]);

  // For WhatsApp sends, resolve the active account once so we can pass
  // whatsappAccountId to the new /whatsapp/messages endpoint.
  useEffect(() => {
    if (channel !== 'whatsapp') return;
    api.get('/whatsapp/accounts')
      .then((r) => {
        const accounts: any[] = Array.isArray(r.data) ? r.data : [];
        const connected = accounts.find((a) => a.status === 'CONNECTED') ?? accounts[0];
        waAccountIdRef.current = connected?.id ?? null;
      })
      .catch(() => { waAccountIdRef.current = null; })
      .finally(() => setWaAccountLoading(false));
  }, [channel]);

  // Esc to dismiss — every other modal in the app supports this so users
  // muscle-memory expect it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !sending && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, sending]);

  const handleSend = async () => {
    if (!message.trim()) {
      toast('error', 'Message body is required');
      return;
    }
    if (!phone.trim()) {
      toast('error', 'Lead has no phone number');
      return;
    }
    setSending(true);
    try {
      if (channel === 'whatsapp') {
        // New multi-tenant API — requires whatsappAccountId + E.164 toWaId
        const waId = phone.replace(/\D/g, ''); // strip non-digits
        if (!waAccountIdRef.current) {
          toast('error', 'No connected WhatsApp account found. Please connect one in Settings → Integrations.');
          return;
        }
        await api.post('/whatsapp/messages', {
          whatsappAccountId: waAccountIdRef.current,
          toWaId: waId,
          type: 'text',
          content: { body: message.trim() },
          leadId,
          contactName: leadName,
        });
      } else {
        // SMS — uses existing endpoint unchanged
        await api.post(meta.endpoint, {
          phone,
          [meta.bodyKey]: message.trim(),
          leadId,
        });
      }
      toast('success', `${meta.label} sent`);
      onSent?.();
      onClose();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        `Failed to send ${channel === 'sms' ? 'SMS' : 'WhatsApp message'}`;
      toast('error', msg);
    } finally {
      setSending(false);
    }
  };

  const segments = channel === 'sms' ? Math.max(1, Math.ceil(message.length / 160)) : 1;
  const overLimit = message.length > meta.charLimit;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !sending && onClose()} />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl ${meta.bgColor} flex items-center justify-center ring-2 ${meta.ringColor}`}>
              <Icon className={`h-5 w-5 ${meta.iconColor}`} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{meta.label}</h2>
              <p className="text-xs text-slate-500">to <span className="font-semibold text-slate-700">{leadName}</span> · {phone}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            className="text-slate-400 hover:text-slate-600 p-1 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {channel === 'sms' && smsTemplates.length > 0 && (
            <div>
              <label className="text-sm font-normal text-slate-700 mb-1.5 block">Template</label>
              <SelectField
                defaultValue=""
                onChange={(e) => {
                  const tpl = smsTemplates.find((t) => t.id === e.target.value);
                  if (tpl) setMessage(tpl.body);
                }}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 h-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 focus:border-[#6366F1]"
              >
                <option value="">Start from a template…</option>
                {smsTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </SelectField>
              <p className="mt-1 text-[11px] text-slate-400">Picks fill the message below — you can still edit before sending. Variables like {'{{lead.name}}'} are not substituted for manual sends.</p>
            </div>
          )}
          <div>
            <label className="text-sm font-normal text-slate-700 mb-1.5 block">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={channel === 'sms' ? 'Type your SMS message…' : 'Type your WhatsApp message…'}
              rows={5}
              autoFocus
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 focus:border-[#6366F1] resize-none transition-all"
            />
            <div className="mt-1.5 flex items-center justify-between text-xs">
              <span className="text-slate-500">{meta.charBucketHelp}</span>
              <span className={`font-mono font-semibold ${overLimit ? 'text-red-600' : 'text-slate-500'}`}>
                {message.length}/{meta.charLimit}
                {channel === 'sms' && message.length > 0 && (
                  <span className="ml-2 text-[#6366F1]">{segments} {segments === 1 ? 'segment' : 'segments'}</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 bg-slate-50/40 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={sending || waAccountLoading || !message.trim() || overLimit}
            className="shadow-md shadow-indigo-200/40"
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-4 w-4 mr-1.5" /> {meta.label}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
